/**
 * Allocation Events API — Write functions
 *
 * Creates events in the allocation_events table.
 * Reads are handled by useAircraftMovementLog.ts (existing hook).
 * The DB trigger (sync_allocation_presence) handles updating allocations.presence.
 */

import { supabase } from '@/lib/supabase'

export interface CreateAllocationEventData {
  allocation_id: string
  aircraft_id: string
  location_id: string
  event_type: 'placed' | 'arrived' | 'departed' | 'departed_final' | 'moved' | 'unit_changed'
  event_source?: 'manual' | 'flightaware' | 'adsb' | 'system' | 'schedule'
  area_id?: string | null
  unit_id?: string | null
  position_x?: number | null
  position_y?: number | null
  position_z?: number | null
  rotation?: number | null
  expected_return_at?: string | null
  event_at?: string | null
  notes?: string | null
}

export interface AllocationEventResult {
  id: string
  allocation_id: string
  event_type: string
  event_at: string
}

/**
 * Create an allocation event.
 * The DB trigger will automatically update the parent allocation's
 * presence, expected_return_at, and status fields.
 */
export async function createAllocationEvent(
  organizationId: string,
  data: CreateAllocationEventData
): Promise<AllocationEventResult> {
  const { data: created, error } = await supabase
    .from('allocation_events')
    .insert({
      organization_id: organizationId,
      allocation_id: data.allocation_id,
      aircraft_id: data.aircraft_id,
      location_id: data.location_id,
      event_type: data.event_type,
      event_source: data.event_source ?? 'manual',
      area_id: data.area_id ?? null,
      unit_id: data.unit_id ?? null,
      position_x: data.position_x ?? null,
      position_y: data.position_y ?? null,
      position_z: data.position_z ?? null,
      rotation: data.rotation ?? null,
      expected_return_at: data.expected_return_at ?? null,
      ...(data.event_at ? { event_at: data.event_at } : {}),
      notes: data.notes ?? null,
    })
    .select('id, allocation_id, event_type, event_at')
    .single()

  if (error) throw error
  return created as AllocationEventResult
}
